import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';

@Injectable()
export class NavigationService {
    private router = inject(Router);
    private location = inject(Location);

    private history: string[] = [];

    constructor() {
        // No error handler needed: router.events is an internal Router stream, not
        // a network call, and does not error under normal operation.
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.history.push(event.urlAfterRedirects);
            }
        });
    }

    back(fallback: (string | number)[], fallbackExtras?: NavigationExtras, blacklist?: string[]) {
        this.history.pop();
        // Go back when:
        // - there is history to go back to
        // - the urls in history are not blacklisted
        if (
            this.history.length > 0 &&
            (!blacklist || !blacklist.find((url) => url === this.history[this.history.length - 1]))
        ) {
            this.location.back();
        } else {
            this.router.navigate(fallback, fallbackExtras);
        }
    }

    getPreviousPage(): string {
        if (this.history.length > 1) return this.history[this.history.length - 2];
        return null;
    }

    goToEmployees(usergroupHash: string) {
        this.router.navigate(['group', usergroupHash, 'employees']);
    }

    goToEvent(eventId: number) {
        this.router.navigate(['/event', eventId]);
    }

    goToEvents(usergroupHash: string) {
        this.router.navigate(['/group', usergroupHash, 'events']);
    }

    goToEventEdit(usergroupHash: string, eventId: number) {
        this.router.navigate(['/group', usergroupHash, 'events', eventId, 'edit']);
    }

    goToEventRegistrations(usergroupHash: string, eventId: number, eventName?: string) {
        const queryParams = { selectedEvent: eventId };
        if (eventName) queryParams['event_name'] = eventName;
        this.router.navigate(['/group', usergroupHash, 'registrations'], { queryParams });
    }

    goToInsurancePolicy(usergroupHash: string, policy: number) {
        this.router.navigate(['/group', usergroupHash, 'insurance', policy]);
    }

    goToInsurancePoliciesForCoAdmin(usergroupHash: string) {
        this.router.navigate(['/group', usergroupHash, 'insurance']);
    }

    goToInsuranceReport(usergroupHash: string, policy: number) {
        this.router.navigate(['/group', usergroupHash, 'insurance', 'report'], {
            queryParams: { policy },
        });
    }

    goToInsuranceReportForManager(usergroupHash: string, policy: number) {
        this.router.navigate(['/group', usergroupHash, 'insurance', 'manager', 'report'], {
            queryParams: { policy },
        });
    }

    goToServiceGroups(usergroupHash: string) {
        this.router.navigate(['/group', usergroupHash, 'servicegroups']);
    }

    goToServiceTickets(usergroupHash: string, serviceGroupName: string) {
        this.router.navigate(['/group', usergroupHash, 'services'], { queryParams: { serviceGroupName } });
    }

    goToTicketEdit(usergroupHash: string, ticketId: number) {
        this.router.navigate(['group', usergroupHash, 'services', ticketId, 'edit']);
    }

    goToServiceTicketEdit(usergroupHash: string, serviceGroupId: number) {
        this.router.navigate(['group', usergroupHash, 'servicegroups', serviceGroupId, 'edit']);
    }

    goToEventTickets(usergroupHash: string, parentName: string) {
        this.router.navigate(['/group', usergroupHash, 'events-tickets'], { queryParams: { parentName } });
    }

    goToTicketAdd(usergroupHash: string, eventId: number, subEventId?: number) {
        const queryParams = { selectedEvent: eventId };
        if (subEventId) queryParams['childEvent'] = subEventId;
        this.router.navigate(['group', usergroupHash, 'events-tickets', 'add'], { queryParams });
    }

    goToEventTicketEdit(usergroupHash: string, ticketId: number, eventId: number) {
        this.router.navigate(['group', usergroupHash, 'events-tickets', ticketId, 'event', eventId, 'edit']);
    }

    goToSubEvents(usergroupHash: string, parentEventId: number) {
        this.router.navigate(['/group', usergroupHash, 'events', parentEventId, 'subevents']);
    }

    goToAddSubEvent(usergroupHash: string, parentEventId: number, scrollUp = false) {
        this.router.navigate(
            ['group', usergroupHash, 'events', parentEventId, 'subevents', 'add'],
            scrollUp ? { queryParams: { scrollUp: '' } } : null,
        );
    }

    goToEditSubEvent(usergroupHash: string, parentEventId: number, childEventId: number) {
        this.router.navigate(['/group', usergroupHash, 'events', parentEventId, 'subevents', childEventId, 'edit']);
    }

    goToUserCompensations(usergroup: string) {
        this.router.navigate(['/me/compensations'], { queryParams: { usergroup } });
    }

    goToCompensations(usergroupHash: string) {
        this.router.navigate(['/group', usergroupHash, 'compensations']);
    }
}
